const PARTNER_KEYS = ['partners', 'selectedPartners', 'partnerXrefs', 'leadPartners', 'partnerLeadXrefs'];
export function createLoadingLeadDrawer(id) {
    return {
        title: 'Lead #' + id,
        isLoading: true,
        fields: [],
        identityFields: [],
        submissionFields: [],
        sourceFields: [],
        partners: [],
        rawJson: '',
    };
}
export function createLeadDrawer(rawResponse, lookups) {
    const lead = extractLead(rawResponse);
    const id = pick(lead.id, lead.leadId);
    const email = pick(lead.email, lead.emailAddress);
    const fieldGroups = buildFieldGroups(lead, lookups);
    const fields = [
        ...fieldGroups.identityFields,
        ...fieldGroups.submissionFields,
        ...fieldGroups.sourceFields,
    ];
    return {
        title: hasValue(email)
            ? String(email)
            : (hasValue(id) ? 'Lead #' + compact(id) : 'Lead Details'),
        isLoading: false,
        fields,
        identityFields: fieldGroups.identityFields,
        submissionFields: fieldGroups.submissionFields,
        sourceFields: fieldGroups.sourceFields,
        partners: buildPartners(lead, lookups),
        rawJson: JSON.stringify(rawResponse ?? {}, null, 2),
    };
}
function extractLead(response) {
    if (!response || typeof response !== 'object') {
        return {};
    }
    const nested = response.data?.lead ?? response.lead ?? response.bean;
    if (nested && typeof nested === 'object') {
        return nested;
    }
    if (response.data && typeof response.data === 'object' && !Array.isArray(response.data)) {
        return response.data;
    }
    return response;
}
function buildFieldGroups(lead, lookups) {
    const channelId = pick(lead.channelId, lead.channel?.id);
    const subunitId = pick(lead.subunitId, lead.subunit?.id);
    return {
        identityFields: mapFields([
            ['ID', pick(lead.id, lead.leadId)],
            ['First Name', pick(lead.firstName, lead.firstname, lead.first_name)],
            ['Last Name', pick(lead.lastName, lead.lastname, lead.last_name)],
            ['Email', pick(lead.email, lead.emailAddress)],
        ]),
        submissionFields: mapFields([
            ['Created Date', formatDateTime(pick(lead.createdDate, lead.createdAt, lead.dateCreated, lead.date))],
            ['Channel ID', channelId],
            ['Subunit ID', subunitId],
            ['Vendor Lead ID', pick(lead.vendorLeadId, lead.vendorId, lead.externalId)],
        ]),
        sourceFields: mapFields([
            ['Internal Source', pick(lead.internalSource, lead.internal_source, lead.psrc, lead.source)],
            ['External Source', pick(lead.externalSource, lead.external_source, lead.trackingSource)],
            ['Zip Code', pick(lead.zipCode, lead.zip, lead.postalCode, lead.postal_code)],
            ['State', pick(lead.state, lead.stateCode, lead.state_code)],
            ['Country', pick(lead.country, lead.countryCode, lead.country_code)],
        ]),
    };
}
function mapFields(fields) {
    return fields.map(([label, value]) => ({
        label,
        value: display(value),
    }));
}
function buildPartners(lead, lookups) {
    const partners = PARTNER_KEYS.map((key) => lead?.[key]).find(Array.isArray) || [];
    return partners.map((partnerXref) => {
        const partnerId = pick(partnerXref.partnerId, partnerXref.id?.partnerId, partnerXref.partner?.id, partnerXref.id);
        const mapped = lookups.partners.get(Number(partnerId));
        const name = pick(partnerXref.partnerInternalName, partnerXref.partnerName, partnerXref.internalName, partnerXref.name, partnerXref.partner?.internalName, partnerXref.partner?.name, mapped?.internalName, mapped?.name);
        return {
            id: hasValue(partnerId) ? String(partnerId) : '',
            name: hasValue(name) ? String(name) : 'Partner',
            transmissionId: optional(pick(partnerXref.transmissionId, partnerXref.transmission?.id)),
            sendFlag: optional(pick(partnerXref.sendFlag, partnerXref.sendFlagName, partnerXref.leadSendFlag)),
            campaignCode: optional(pick(partnerXref.campaignCode, partnerXref.campaign_code)),
            status: optional(pick(partnerXref.status, partnerXref.statusName, partnerXref.transmissionStatus)),
        };
    });
}
function pick(...values) {
    return values.find(hasValue) ?? null;
}
function hasValue(value) {
    return value !== undefined && value !== null && String(value).trim() !== '';
}
function compact(value) {
    if (typeof value === 'boolean') {
        return value ? 'Yes' : 'No';
    }
    if (Array.isArray(value)) {
        return value.map(compact).join(', ');
    }
    if (typeof value === 'object' && value !== null) {
        return JSON.stringify(value);
    }
    return String(value);
}
function display(value) {
    return hasValue(value) ? compact(value) : '-';
}
function optional(value) {
    return hasValue(value) ? compact(value) : '';
}
function nameWithId(name, id) {
    if (hasValue(name) && hasValue(id)) {
        return String(name) + ' (ID ' + id + ')';
    }
    if (hasValue(name)) {
        return String(name);
    }
    return hasValue(id) ? 'ID ' + id : '';
}
function formatDateTime(value) {
    if (!hasValue(value)) {
        return '';
    }
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
}
